
import { supabase } from "@/integrations/supabase/client";
import { handleServiceError } from "../utils/errorHandling";
import { CurrentUser } from "@/types/auth";
import { FamilyInviteResult } from "@/types/database";
import { familyInviteService } from "@/services/family";

/**
 * Service for authentication query operations (getting user data)
 */
export const authQueryService = {
  /**
   * Clean up auth state by removing all Supabase tokens from storage
   */
  cleanupAuthState: async () => {
    const { cleanupAuthState } = await import("@/utils/authCleanup");
    return cleanupAuthState();
  },

  /**
   * Get the current authenticated user
   */
  getCurrentUser: async (): Promise<CurrentUser> => {
    try {
      const { data, error } = await supabase.auth.getUser();
      
      if (error) throw error;
      
      return { 
        user: data.user, 
        userId: data.user?.id || null,
        error: null 
      };
    } catch (error) {
      // Don't show toast for this common operation
      console.error("Error getting current user:", error);
      return { 
        user: null, 
        userId: null,
        error: error instanceof Error ? error : new Error('Unknown error') 
      };
    }
  },

  /**
   * Get the current user's profile
   */
  getUserProfile: async (): Promise<any> => {
    try {
      const { user, error } = await authQueryService.getCurrentUser();
      
      if (error || !user) throw error || new Error("No authenticated user");
      
      const { data, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (profileError) throw profileError;
      
      return data;
    } catch (error) {
      handleServiceError(error, "Failed to get user profile");
      return null;
    }
  },

  /**
   * Get the current user's family ID using current_family_id
   */
  getCurrentUserFamilyId: async (): Promise<string | null> => {
    try {
      const { user, error } = await authQueryService.getCurrentUser();
      
      if (error || !user) throw error || new Error("No authenticated user");
      
      const { data, error: profileError } = await supabase
        .from('profiles')
        .select('current_family_id')
        .eq('id', user.id)
        .single();
      
      if (profileError) throw profileError;
      
      return data?.current_family_id || null;
    } catch (error) {
      console.error("Error getting current user family ID:", error);
      return null;
    }
  },

  /**
   * Join a family using an invite code
   */
  joinFamily: async (inviteCode: string): Promise<FamilyInviteResult> => {
    try {
      const { user, error } = await authQueryService.getCurrentUser();
      
      if (error || !user) throw error || new Error("No authenticated user");
      
      const { data, error: joinError } = await supabase
        .rpc('join_family_by_invite', { 
          p_user_id: user.id, 
          p_invite_code: inviteCode.trim() 
        });
      
      if (joinError) throw joinError;
      
      return { 
        success: true, 
        message: "Successfully joined family!", 
        familyId: data 
      };
    } catch (error: any) {
      handleServiceError(error, "Failed to join family");
      
      // Return a structured error response
      return { 
        success: false, 
        message: error.message || "An error occurred while joining the family"
      };
    }
  },
};
